import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, ChevronLeft, ChevronRight, CalendarDays, Clock, TrendingUp, Crown, Baby, Briefcase, Loader2, X, Check, ExternalLink, RefreshCw, Link2, Trash2, MapPin } from 'lucide-react';

const fmtUK = (d) => { if (!d) return ''; const p = d.split('-'); return p.length === 3 ? `${p[2]}/${p[1]}/${p[0]}` : d; };

const API = process.env.REACT_APP_BACKEND_URL;

const TYPE_CONFIG = {
  task: { label: 'Task', color: '#B8860B' },
  personal: { label: 'Personal', color: '#EC4899' },
  hmrc: { label: 'HMRC', color: '#D97706' },
  opportunity: { label: 'Opportunity', color: '#0D9488' },
  calendar_feed: { label: 'Calendar', color: '#0EA5E9' },
};

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const DAYS = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'];

const UnifiedPlanner = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('week');
  const [weekOffset, setWeekOffset] = useState(0);
  const [monthOffset, setMonthOffset] = useState(0);
  const [filter, setFilter] = useState('all');
  const [selectedDay, setSelectedDay] = useState(null);
  const [completing, setCompleting] = useState(null);
  const [detailItem, setDetailItem] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [feeds, setFeeds] = useState([]);
  const [showFeeds, setShowFeeds] = useState(false);
  const [syncing, setSyncing] = useState(null);

  const fetchItems = () => {
    axios.get(`${API}/api/planner/unified?months=8`, { withCredentials: true })
      .then(res => setItems(res.data.items || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  const fetchFeeds = () => {
    axios.get(`${API}/api/calendar-feeds`, { withCredentials: true })
      .then(res => setFeeds(res.data))
      .catch(console.error);
  };

  const syncFeed = async (feedId) => {
    setSyncing(feedId);
    try {
      await axios.post(`${API}/api/calendar-feeds/${feedId}/sync`, {}, { withCredentials: true });
      fetchFeeds();
      fetchItems();
    } catch (err) { console.error(err); }
    setSyncing(null);
  };

  const deleteFeed = async (feedId) => {
    if (!window.confirm('Remove this calendar subscription?')) return;
    try {
      await axios.delete(`${API}/api/calendar-feeds/${feedId}`, { withCredentials: true });
      fetchFeeds();
      fetchItems();
    } catch (err) { console.error(err); }
  };

  useEffect(() => { fetchItems(); fetchFeeds(); }, []);

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const getDateStr = (d) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;

  const todayStr = getDateStr(today);

  const filtered = useMemo(() => {
    if (filter === 'all') return items;
    return items.filter(i => i.type === filter);
  }, [items, filter]);

  const weekStart = useMemo(() => {
    const d = new Date(today);
    d.setDate(d.getDate() - ((d.getDay() + 6) % 7) + weekOffset * 7);
    return d;
  }, [weekOffset, todayStr]);

  const weekDays = useMemo(() => Array.from({ length: 7 }, (_, i) => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + i);
    return d;
  }), [weekStart]);

  const weekLabel = `${weekDays[0].toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })} — ${weekDays[6].toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}`;

  const monthDate = useMemo(() => {
    const d = new Date(today.getFullYear(), today.getMonth() + monthOffset, 1);
    return d;
  }, [monthOffset, todayStr]);

  const monthGrid = useMemo(() => {
    const first = new Date(monthDate.getFullYear(), monthDate.getMonth(), 1);
    const startDay = (first.getDay() + 6) % 7;
    const daysInMonth = new Date(monthDate.getFullYear(), monthDate.getMonth() + 1, 0).getDate();
    const grid = [];
    for (let i = 0; i < startDay; i++) grid.push(null);
    for (let d = 1; d <= daysInMonth; d++) grid.push(new Date(monthDate.getFullYear(), monthDate.getMonth(), d));
    return grid;
  }, [monthDate]);

  const typePriority = { personal: 0, hmrc: 1, opportunity: 2, task: 3 };
  const getItemsForDate = (dateStr) => filtered
    .filter(i => i.date === dateStr)
    .sort((a, b) => {
      // Time-based items first, then personal > hmrc > opportunity > task
      if (a.time && !b.time) return -1;
      if (!a.time && b.time) return 1;
      if (a.time && b.time) return a.time.localeCompare(b.time);
      return (typePriority[a.type] || 3) - (typePriority[b.type] || 3);
    });

  const completeItem = async (item) => {
    setCompleting(item.id);
    try {
      if (item.type === 'task') {
        await axios.put(`${API}/api/tasks/${item.id}`, { status: 'completed' }, { withCredentials: true });
      } else if (item.type === 'personal') {
        await axios.put(`${API}/api/personal/${item.id}`, { status: 'done' }, { withCredentials: true });
      } else if (item.type === 'hmrc') {
        await axios.patch(`${API}/api/hmrc/deadlines/${item.id}`, { status: 'filed' }, { withCredentials: true });
      }
      setItems(prev => prev.filter(i => i.id !== item.id));
    } catch (err) { console.error(err); }
    setCompleting(null);
  };

  const openDetail = (item) => {
    setDetailItem(item);
    if (item.type === 'task') {
      setEditForm({ title: item.title || '', due_date: item.date || '', time: item.time || '', description: item.description || '', priority: item.priority || 'medium', status: item.status || 'open' });
    } else if (item.type === 'personal') {
      setEditForm({ title: item.title || '', date: item.date || '', time: item.time || '', notes: item.notes || item.description || '', status: item.status || 'active' });
    } else {
      setEditForm({ title: item.title || '', date: item.date || '' });
    }
  };

  const saveDetail = async () => {
    if (!detailItem) return;
    setSaving(true);
    try {
      if (detailItem.type === 'task') {
        await axios.put(`${API}/api/tasks/${detailItem.id}`, {
          title: editForm.title, due_date: editForm.due_date || null, time: editForm.time || null,
          description: editForm.description || '', priority: editForm.priority, status: editForm.status,
        }, { withCredentials: true });
      } else if (detailItem.type === 'personal') {
        await axios.put(`${API}/api/personal/${detailItem.id}`, {
          title: editForm.title, date: editForm.date || null, time: editForm.time || null,
          notes: editForm.notes || '', status: editForm.status,
        }, { withCredentials: true });
      }
      setDetailItem(null);
      fetchItems();
    } catch (err) { console.error(err); }
    setSaving(false);
  };

  const deleteDetail = async () => {
    if (!detailItem || !window.confirm(`Delete "${detailItem.title}"?`)) return;
    try {
      if (detailItem.type === 'task') {
        await axios.delete(`${API}/api/tasks/${detailItem.id}`, { withCredentials: true });
      } else if (detailItem.type === 'personal') {
        await axios.delete(`${API}/api/personal/${detailItem.id}`, { withCredentials: true });
      }
      setDetailItem(null);
      fetchItems();
    } catch (err) { console.error(err); }
  };

  const MAX_VISIBLE = 6;

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#FAF9F7' }}>
      <Loader2 className="w-8 h-8 animate-spin" style={{ color: '#D4AF37' }} />
    </div>
  );

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#FAF9F7' }} data-testid="unified-planner">
      <header className="bg-white px-6 lg:px-10 py-5" style={{ borderBottom: '1px solid #E5E1DB' }}>
        <div className="max-w-7xl mx-auto">
          <button onClick={() => navigate('/dashboard')} className="flex items-center gap-1.5 text-xs mb-3 hover:opacity-70" style={{ color: '#8B7355' }} data-testid="back-btn">
            <ArrowLeft className="w-3.5 h-3.5" /> Dashboard
          </button>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-3">
              <CalendarDays className="w-6 h-6" style={{ color: '#D4AF37' }} />
              <div>
                <h1 className="text-2xl font-light tracking-tight" style={{ color: '#2C1A0E' }}>Planner</h1>
                <p className="text-xs" style={{ color: '#8B7355' }}>{items.length} items · click to view details</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <select value={filter} onChange={e => setFilter(e.target.value)} className="px-3 py-1.5 text-xs border rounded-sm" style={{ borderColor: '#E5E1DB', color: '#2C1A0E' }} data-testid="planner-filter">
                <option value="all">All</option>
                <option value="task">Tasks</option>
                <option value="personal">Personal</option>
                <option value="hmrc">HMRC</option>
                <option value="opportunity">Opportunities</option>
                <option value="calendar_feed">Calendar Feeds</option>
              </select>
              <button onClick={() => setShowFeeds(!showFeeds)} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border rounded-sm transition-all hover:shadow-sm" style={{ borderColor: showFeeds ? '#0EA5E9' : '#E5E1DB', backgroundColor: showFeeds ? '#0EA5E910' : '#FFF', color: showFeeds ? '#0EA5E9' : '#8B7355' }} data-testid="feeds-btn">
                <Link2 className="w-3.5 h-3.5" /> Feeds {feeds.length > 0 && <span className="text-[9px] px-1 py-0 rounded-full" style={{ backgroundColor: '#0EA5E920', color: '#0EA5E9' }}>{feeds.length}</span>}
              </button>
              {['week', 'month'].map(v => (
                <button key={v} onClick={() => setView(v)} className="px-3 py-1.5 text-xs font-medium rounded-sm border transition-all"
                  style={{ borderColor: view === v ? '#D4AF37' : '#E5E1DB', backgroundColor: view === v ? '#D4AF3715' : '#FFF', color: view === v ? '#B8860B' : '#8B7355' }}
                  data-testid={`view-${v}`}>
                  {v === 'week' ? 'Week' : 'Month'}
                </button>
              ))}
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 lg:px-10 py-6">
        {/* Legend */}
        <div className="flex flex-wrap gap-4 mb-5">
          {Object.entries(TYPE_CONFIG).map(([key, cfg]) => (
            <div key={key} className="flex items-center gap-1.5">
              <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: cfg.color }} />
              <span className="text-[10px] uppercase tracking-wider" style={{ color: '#8B7355' }}>{cfg.label}</span>
            </div>
          ))}
        </div>

        {/* Calendar Feeds Panel */}
        {showFeeds && (
          <div className="bg-white border rounded-sm mb-5 overflow-hidden" style={{ borderColor: '#E5E1DB' }} data-testid="feeds-panel">
            <div className="px-5 py-3 flex items-center justify-between" style={{ borderBottom: '1px solid #E5E1DB', backgroundColor: '#F0F9FF' }}>
              <div className="flex items-center gap-2">
                <Link2 className="w-4 h-4" style={{ color: '#0EA5E9' }} />
                <h3 className="text-xs font-semibold uppercase tracking-wider" style={{ color: '#0EA5E9' }}>Calendar Subscriptions</h3>
              </div>
              <button onClick={() => setShowFeeds(false)} className="p-1 hover:bg-gray-100 rounded"><X className="w-3.5 h-3.5" style={{ color: '#8B7355' }} /></button>
            </div>
            {feeds.length === 0 ? (
              <div className="px-5 py-6 text-center">
                <p className="text-xs" style={{ color: '#8B7355' }}>No calendar feeds subscribed yet.</p>
              </div>
            ) : (
              feeds.map(feed => (
                <div key={feed.id} className="px-5 py-3 flex items-center gap-3" style={{ borderBottom: '1px solid #F5F1EB' }} data-testid={`feed-${feed.id}`}>
                  <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: feed.color || '#0EA5E9' }} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium" style={{ color: '#2C1A0E' }}>{feed.name}</p>
                    <p className="text-[10px] truncate" style={{ color: '#8B7355' }}>
                      {feed.event_count || 0} events{feed.last_synced ? ` · synced ${new Date(feed.last_synced).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}` : ' · not synced'}
                    </p>
                  </div>
                  <button onClick={() => syncFeed(feed.id)} disabled={syncing === feed.id} className="p-1.5 rounded-sm hover:bg-blue-50 transition-colors" title="Sync now" data-testid={`sync-feed-${feed.id}`}>
                    {syncing === feed.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" style={{ color: '#0EA5E9' }} /> : <RefreshCw className="w-3.5 h-3.5" style={{ color: '#0EA5E9' }} />}
                  </button>
                  <button onClick={() => deleteFeed(feed.id)} className="p-1.5 rounded-sm hover:bg-red-50 transition-colors" title="Remove" data-testid={`delete-feed-${feed.id}`}>
                    <Trash2 className="w-3.5 h-3.5" style={{ color: '#DC2626' }} />
                  </button>
                </div>
              ))
            )}
          </div>
        )}

        {/* WEEK VIEW */}
        {view === 'week' && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <button onClick={() => setWeekOffset(w => w - 1)} className="p-1.5 rounded-sm hover:bg-amber-50" data-testid="week-prev"><ChevronLeft className="w-4 h-4" style={{ color: '#8B7355' }} /></button>
              <div className="text-center">
                <p className="text-sm font-medium" style={{ color: '#2C1A0E' }}>{weekLabel}</p>
                {weekOffset !== 0 && <button onClick={() => setWeekOffset(0)} className="text-[10px] underline" style={{ color: '#B8860B' }}>Today</button>}
              </div>
              <button onClick={() => setWeekOffset(w => w + 1)} className="p-1.5 rounded-sm hover:bg-amber-50" data-testid="week-next"><ChevronRight className="w-4 h-4" style={{ color: '#8B7355' }} /></button>
            </div>
            <div className="grid grid-cols-7 gap-2">
              {weekDays.map((day, i) => {
                const dateStr = getDateStr(day);
                const dayItems = getItemsForDate(dateStr);
                const isToday = dateStr === getDateStr(today);
                const showAll = selectedDay === dateStr;
                const visibleItems = showAll ? dayItems : dayItems.slice(0, MAX_VISIBLE);
                const overflow = dayItems.length - MAX_VISIBLE;
                return (
                  <div key={i} className="bg-white border rounded-sm min-h-[200px] flex flex-col" style={{ borderColor: isToday ? '#D4AF37' : '#E5E1DB' }} data-testid={`week-day-${i}`}>
                    <div className="px-2 py-1.5 flex items-center justify-between cursor-pointer" style={{ borderBottom: '1px solid #F5F1EB', backgroundColor: isToday ? '#D4AF3710' : '#FDFCFB' }}
                      onClick={() => setSelectedDay(selectedDay === dateStr ? null : dateStr)}>
                      <div>
                        <p className="text-[10px] uppercase tracking-wider" style={{ color: isToday ? '#B8860B' : '#8B7355' }}>{DAYS[i]}</p>
                        <p className="text-sm font-medium" style={{ color: isToday ? '#B8860B' : '#2C1A0E' }}>{day.getDate()}</p>
                      </div>
                      {dayItems.length > 0 && (
                        <span className="text-[9px] px-1.5 py-0.5 rounded-full" style={{ backgroundColor: isToday ? '#D4AF3720' : '#F5F1EB', color: '#8B7355' }}>{dayItems.length}</span>
                      )}
                    </div>
                    <div className="px-1.5 py-1 space-y-0.5 flex-1 overflow-y-auto" style={{ maxHeight: showAll ? '400px' : '180px' }}>
                      {visibleItems.map(item => (
                        <div key={item.id} className="flex items-center gap-1 py-0.5 group cursor-pointer hover:bg-amber-50/50 rounded-sm px-0.5"
                          onClick={() => openDetail(item)}
                          data-testid={`planner-item-${item.id}`}>
                          {(item.type === 'task' || item.type === 'personal') && (
                            <button onClick={(e) => { e.stopPropagation(); completeItem(item); }}
                              className="w-3.5 h-3.5 rounded-sm border flex-shrink-0 flex items-center justify-center hover:bg-green-100 transition-colors"
                              style={{ borderColor: item.color }}
                              data-testid={`complete-${item.id}`}>
                              {completing === item.id ? <Loader2 className="w-2 h-2 animate-spin" style={{ color: item.color }} /> : <Check className="w-2 h-2 opacity-0 group-hover:opacity-40" style={{ color: '#16A34A' }} />}
                            </button>
                          )}
                          <div className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: item.color }} />
                          <span className="text-[11px] truncate" style={{ color: '#2C1A0E' }}>
                            {item.time && <span className="font-mono mr-1" style={{ color: '#8B7355', fontSize: '9px' }}>{item.time}</span>}
                            {item.title}
                          </span>
                        </div>
                      ))}
                      {!showAll && overflow > 0 && (
                        <button onClick={() => setSelectedDay(dateStr)} className="text-[10px] font-medium w-full text-left px-1 py-0.5" style={{ color: '#B8860B' }}>
                          +{overflow} more
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* MONTH VIEW */}
        {view === 'month' && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <button onClick={() => setMonthOffset(m => m - 1)} className="p-1.5 rounded-sm hover:bg-amber-50" data-testid="month-prev"><ChevronLeft className="w-4 h-4" style={{ color: '#8B7355' }} /></button>
              <div className="text-center">
                <p className="text-sm font-medium" style={{ color: '#2C1A0E' }}>{MONTHS[monthDate.getMonth()]} {monthDate.getFullYear()}</p>
                {monthOffset !== 0 && <button onClick={() => setMonthOffset(0)} className="text-[10px] underline" style={{ color: '#B8860B' }}>This month</button>}
              </div>
              <button onClick={() => setMonthOffset(m => m + 1)} className="p-1.5 rounded-sm hover:bg-amber-50" data-testid="month-next"><ChevronRight className="w-4 h-4" style={{ color: '#8B7355' }} /></button>
            </div>
            <div className="grid grid-cols-7 gap-px bg-white border rounded-sm overflow-hidden" style={{ borderColor: '#E5E1DB' }}>
              {DAYS.map(d => (
                <div key={d} className="px-2 py-1.5 text-center" style={{ backgroundColor: '#FDFCFB', borderBottom: '1px solid #E5E1DB' }}>
                  <span className="text-[10px] uppercase tracking-wider" style={{ color: '#8B7355' }}>{d}</span>
                </div>
              ))}
              {monthGrid.map((day, i) => {
                if (!day) return <div key={`e-${i}`} style={{ backgroundColor: '#FAFAF9', borderBottom: '1px solid #F5F1EB' }} />;
                const dateStr = getDateStr(day);
                const dayItems = getItemsForDate(dateStr);
                const isToday = dateStr === getDateStr(today);
                return (
                  <div key={i} className="min-h-[90px] p-1 cursor-pointer hover:bg-amber-50/20 transition-colors" onClick={() => setSelectedDay(selectedDay === dateStr ? null : dateStr)}
                    style={{ backgroundColor: isToday ? '#D4AF3708' : '#FFF', borderBottom: '1px solid #F5F1EB', borderRight: '1px solid #F5F1EB' }} data-testid={`month-day-${day.getDate()}`}>
                    <div className="flex items-center justify-between">
                      <p className="text-xs font-medium" style={{ color: isToday ? '#B8860B' : '#2C1A0E' }}>{day.getDate()}</p>
                      {dayItems.length > 0 && <span className="text-[8px] px-1 py-0 rounded-full" style={{ backgroundColor: '#F5F1EB', color: '#8B7355' }}>{dayItems.length}</span>}
                    </div>
                    {dayItems.slice(0, 2).map(item => (
                      <div key={item.id} className="flex items-center gap-1 py-px">
                        <div className="w-1 h-1 rounded-full flex-shrink-0" style={{ backgroundColor: item.color }} />
                        <span className="text-[9px] truncate" style={{ color: '#4A3A2E' }}>{item.title}</span>
                      </div>
                    ))}
                    {dayItems.length > 2 && <p className="text-[8px]" style={{ color: '#B8860B' }}>+{dayItems.length - 2}</p>}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </main>

      {/* Day Detail Panel */}
      {selectedDay && (
        <div className="fixed inset-0 z-50 flex justify-end" style={{ backgroundColor: 'rgba(44,26,14,0.3)' }} onClick={() => setSelectedDay(null)}>
          <div className="w-full max-w-md bg-white h-full overflow-y-auto shadow-2xl" onClick={e => e.stopPropagation()} data-testid="day-panel">
            <div className="px-6 py-4 flex items-center justify-between sticky top-0 bg-white z-10" style={{ borderBottom: '1px solid #E5E1DB' }}>
              <div>
                <p className="text-lg font-light" style={{ color: '#2C1A0E' }}>
                  {new Date(selectedDay + 'T00:00:00').toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' })}
                </p>
                <p className="text-xs" style={{ color: '#8B7355' }}>{getItemsForDate(selectedDay).length} items</p>
              </div>
              <button onClick={() => setSelectedDay(null)} className="p-1.5 hover:bg-amber-50 rounded-sm"><X className="w-4 h-4" style={{ color: '#8B7355' }} /></button>
            </div>
            <div className="p-4 space-y-2">
              {getItemsForDate(selectedDay).map(item => (
                <div key={item.id} className="p-3 rounded-sm border group" style={{ borderColor: '#E5E1DB', borderLeft: `3px solid ${item.color}` }} data-testid={`panel-item-${item.id}`}>
                  <div className="flex items-start gap-3">
                    <button onClick={() => completeItem(item)}
                      className="w-5 h-5 mt-0.5 rounded-sm border flex-shrink-0 flex items-center justify-center hover:bg-green-100 transition-colors"
                      style={{ borderColor: item.color }}
                      data-testid={`panel-complete-${item.id}`}>
                      {completing === item.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3 opacity-0 group-hover:opacity-50" style={{ color: '#16A34A' }} />}
                    </button>
                    <div className="flex-1 min-w-0 cursor-pointer" onClick={() => openDetail(item)}>
                      <p className="text-sm font-medium" style={{ color: '#2C1A0E' }}>{item.title}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-[10px] px-1.5 py-0.5 rounded-sm" style={{ backgroundColor: `${item.color}15`, color: item.color }}>{TYPE_CONFIG[item.type]?.label || item.type}</span>
                        {item.time && <span className="text-[10px] font-mono" style={{ color: '#8B7355' }}><Clock className="w-2.5 h-2.5 inline mr-0.5" />{item.time}</span>}
                        {item.source && <span className="text-[10px]" style={{ color: '#8B7355' }}>{item.source}</span>}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
              {getItemsForDate(selectedDay).length === 0 && (
                <p className="text-sm text-center py-8" style={{ color: '#8B7355' }}>No items for this day</p>
              )}
            </div>
          </div>
        </div>
      )}
      {/* ═══ DETAIL EDIT MODAL ═══ */}
      {detailItem && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-end sm:items-center justify-center" onClick={() => setDetailItem(null)} data-testid="planner-detail-overlay">
          <div className="bg-white w-full sm:w-[420px] sm:rounded-lg rounded-t-2xl shadow-xl max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="planner-detail-modal">
            <div className="flex items-center justify-between px-5 py-3" style={{ borderBottom: '1px solid #E8E0D4' }}>
              <div className="flex items-center gap-2">
                <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: detailItem.color || '#B8860B' }} />
                <h3 className="text-sm font-medium" style={{ color: '#2C1A0E' }}>{TYPE_CONFIG[detailItem.type]?.label || detailItem.type}</h3>
              </div>
              <button onClick={() => setDetailItem(null)} className="p-1 rounded-full hover:bg-gray-100" data-testid="detail-close"><X className="w-4 h-4" style={{ color: '#8B7355' }} /></button>
            </div>
            <div className="px-5 py-4 space-y-3">
              {(detailItem.type === 'task' || detailItem.type === 'personal') ? (
                <>
                  <div>
                    <label className="block text-[10px] uppercase tracking-wider mb-1 font-semibold" style={{ color: '#8B7355' }}>Title</label>
                    <input type="text" value={editForm.title || ''} onChange={e => setEditForm({...editForm, title: e.target.value})} className="w-full px-3 py-2 border text-sm rounded-sm" style={{ borderColor: '#E8E0D4' }} data-testid="detail-title" />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-[10px] uppercase tracking-wider mb-1 font-semibold" style={{ color: '#8B7355' }}>Date</label>
                      <input type="date" value={editForm[detailItem.type === 'task' ? 'due_date' : 'date'] || ''} onChange={e => setEditForm({...editForm, [detailItem.type === 'task' ? 'due_date' : 'date']: e.target.value})} className="w-full px-3 py-2 border text-sm rounded-sm" style={{ borderColor: '#E8E0D4' }} data-testid="detail-date" />
                    </div>
                    <div>
                      <label className="block text-[10px] uppercase tracking-wider mb-1 font-semibold" style={{ color: '#8B7355' }}>Time</label>
                      <input type="time" value={editForm.time || ''} onChange={e => setEditForm({...editForm, time: e.target.value})} className="w-full px-3 py-2 border text-sm rounded-sm" style={{ borderColor: '#E8E0D4' }} data-testid="detail-time" />
                    </div>
                  </div>
                  {detailItem.type === 'task' && (
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="block text-[10px] uppercase tracking-wider mb-1 font-semibold" style={{ color: '#8B7355' }}>Priority</label>
                        <select value={editForm.priority || 'medium'} onChange={e => setEditForm({...editForm, priority: e.target.value})} className="w-full px-3 py-2 border text-sm rounded-sm" style={{ borderColor: '#E8E0D4' }} data-testid="detail-priority">
                          <option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-[10px] uppercase tracking-wider mb-1 font-semibold" style={{ color: '#8B7355' }}>Status</label>
                        <select value={editForm.status || 'open'} onChange={e => setEditForm({...editForm, status: e.target.value})} className="w-full px-3 py-2 border text-sm rounded-sm" style={{ borderColor: '#E8E0D4' }} data-testid="detail-status">
                          <option value="open">Open</option><option value="in_progress">In Progress</option><option value="done">Done</option>
                        </select>
                      </div>
                    </div>
                  )}
                  <div>
                    <label className="block text-[10px] uppercase tracking-wider mb-1 font-semibold" style={{ color: '#8B7355' }}>{detailItem.type === 'task' ? 'Description' : 'Notes'}</label>
                    <textarea value={editForm[detailItem.type === 'task' ? 'description' : 'notes'] || ''} onChange={e => setEditForm({...editForm, [detailItem.type === 'task' ? 'description' : 'notes']: e.target.value})} className="w-full px-3 py-2 border text-sm rounded-sm resize-none" style={{ borderColor: '#E8E0D4' }} rows={3} data-testid="detail-notes" />
                  </div>
                  {detailItem.source && (
                    <p className="text-[10px] px-2 py-1 rounded-sm" style={{ backgroundColor: '#FAF7F2', color: '#8B7355' }}>{detailItem.source}</p>
                  )}
                </>
              ) : (
                <div className="py-2">
                  <h4 className="text-base font-medium mb-2" style={{ color: '#2C1A0E' }}>{detailItem.title}</h4>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <CalendarDays className="w-3.5 h-3.5 flex-shrink-0" style={{ color: '#8B7355' }} />
                      <span className="text-xs" style={{ color: '#2C1A0E' }}>{fmtUK(detailItem.date)}{detailItem.end_date && detailItem.end_date !== detailItem.date ? ` — ${fmtUK(detailItem.end_date)}` : ''}</span>
                    </div>
                    {detailItem.time && (
                      <div className="flex items-center gap-2">
                        <Clock className="w-3.5 h-3.5 flex-shrink-0" style={{ color: '#8B7355' }} />
                        <span className="text-xs" style={{ color: '#2C1A0E' }}>{detailItem.time}</span>
                      </div>
                    )}
                    {detailItem.location && (
                      <div className="flex items-center gap-2">
                        <MapPin className="w-3.5 h-3.5 flex-shrink-0" style={{ color: '#8B7355' }} />
                        <span className="text-xs" style={{ color: '#2C1A0E' }}>{detailItem.location}</span>
                      </div>
                    )}
                    {detailItem.category && (
                      <span className="inline-block text-[10px] px-2 py-0.5 rounded-full" style={{ backgroundColor: `${detailItem.color || '#0EA5E9'}15`, color: detailItem.color || '#0EA5E9' }}>{detailItem.category}</span>
                    )}
                    {detailItem.source && (
                      <p className="text-[10px] px-2 py-1 rounded-sm" style={{ backgroundColor: '#F0F9FF', color: '#0EA5E9' }}>via {detailItem.source}</p>
                    )}
                    {detailItem.description && (
                      <p className="text-xs mt-2 leading-relaxed" style={{ color: '#6B5B4E' }}>{detailItem.description}</p>
                    )}
                  </div>
                </div>
              )}
            </div>
            {(detailItem.type === 'task' || detailItem.type === 'personal') && (
              <div className="flex items-center gap-2 px-5 py-3" style={{ borderTop: '1px solid #E8E0D4' }}>
                <button onClick={deleteDetail} className="px-3 py-2 text-xs font-medium rounded-sm" style={{ backgroundColor: '#FEF2F2', color: '#DC2626', border: '1px solid #FECACA' }} data-testid="detail-delete">Delete</button>
                <div className="flex-1" />
                <button onClick={() => setDetailItem(null)} className="px-4 py-2 text-xs font-medium border rounded-sm" style={{ borderColor: '#E8E0D4', color: '#8B7355' }}>Cancel</button>
                <button onClick={saveDetail} disabled={saving} className="px-4 py-2 text-xs font-medium rounded-sm" style={{ backgroundColor: '#2C1A0E', color: '#D4AF37' }} data-testid="detail-save">{saving ? 'Saving...' : 'Save'}</button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default UnifiedPlanner;
